import React, { useEffect, useState } from 'react';
import { Button, OverlayTrigger, Spinner, Table, Tooltip } from 'react-bootstrap';
import { faPencilAlt, faPlusSquare, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import RegisterChildModal from './RegisterChildModal';
import EditRegisteredChildModal from './EditRegisteredChildModal';
import DeleteRegisteredChildModal from './DeleteRegisteredChildModal';

import { AppDispatch } from '../store/store';
import { fetchChildren } from '../store/getChildrenSlice';
import { RootState } from '../store/rootReducer';
import { Child, ParentInfo } from '../api/account.types';

const Summary = (): React.ReactElement => {
  const { t, i18n } = useTranslation('summary');
  const [showRegisterModal, setShowRegisterModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<Child>();
  const { children } = useSelector((state: RootState) => state.children);
  const dispatch = useDispatch<AppDispatch>();
  const [childrenWithoutRegistration, setChildrenWithoutRegistration] = useState<boolean>(false);

  const parentInfo: ParentInfo = {
    family_number: children?.items[0]?.family_number,
    legal_address: children?.items[0]?.legal_address,
    name: children?.items[0]?.name,
    postal_code: children?.items[0]?.postal_code,
    ssn: children?.items[0]?.ssn,
    town_name: children?.items[0]?.town_name,
  };

  useEffect(() => {
    dispatch(fetchChildren());
  }, [dispatch]);

  const onModalClosed = (created: boolean) => {
    setShowRegisterModal(false);
    if (created) {
      dispatch(fetchChildren());
    }
  };

  const onDeleteModalClosed = (deleted: boolean) => {
    setShowDeleteModal(false);

    if (deleted) {
      dispatch(fetchChildren());
    }
  };

  const onEditModalClosed = (edited: boolean) => {
    setShowEditModal(false);

    if (edited) {
      dispatch(fetchChildren());
    }
  };

  const renderEditButtonTooltip = (props: any) => (
    <Tooltip id="button-tooltip" {...props}>
      {t('editButton')}
    </Tooltip>
  );
  const renderDeleteButtonTooltip = (props: any) => (
    <Tooltip id="button-tooltip" {...props}>
      {t('deleteButton')}
    </Tooltip>
  );

  const onHandleRegisterUser = (user: Child) => {
    setShowRegisterModal(true);
    setCurrentUser(user);
  };

  const onHandleEditUser = (user: Child) => {
    setShowEditModal(true);
    setCurrentUser(user);
  };

  const onHandleDeleteUser = (user: Child) => {
    setShowDeleteModal(true);
    setCurrentUser(user);
  };

  useEffect(() => {
    setChildrenWithoutRegistration(children?.items[0].childrens.filter((i: any) => i.registrations.length !== 0).length === 0);
  }, [children]);

  return (
    <>
      {showEditModal && currentUser && <EditRegisteredChildModal originalValues={currentUser} parentInfo={parentInfo} onClose={onEditModalClosed} />}
      {showDeleteModal && currentUser && <DeleteRegisteredChildModal originalValues={currentUser} onClose={onDeleteModalClosed} />}
      {showRegisterModal && currentUser && <RegisterChildModal child={currentUser} parentInfo={parentInfo} onClose={onModalClosed} />}
      <div className="white-column padding-top-25 overview-text margin-top">
        <h1 className="overview-text-size">{t('summaryTitle')}</h1>
      </div>
      {!children && (
        <div className="spinner">
          <Spinner animation="border" role="status">
            <span className="sr-only">Loading...</span>
          </Spinner>
        </div>
      )}
      {children?.items[0]?.registrations?.length === 0 ? (
        <div>{t('common:noDataFound')}</div>
      ) : (
        children && (
          <div className="white-column">
            <Table className="table table-with-grey-header" hover responsive>
              <thead>
                <tr>
                  <th>{t('child')}</th>
                  <th>{t('school')}</th>
                  <th>{t('registration')}</th>
                  {childrenWithoutRegistration ? null : <th>{t('pinNumber')}</th>}
                  <th> </th>
                </tr>
              </thead>
              <tbody>
                {children?.items[0]?.childrens?.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ fontStyle: 'italic', textAlign: 'center' }}>
                      {t('noChildAvailable')}
                    </td>
                  </tr>
                ) : (
                  children?.items[0].childrens?.map((user: Child) => {
                    return (
                      <tr key={user.ssn}>
                        <td>
                          {user.name}
                          <br />
                          <span className="text-muted">{user.ssn}</span>
                        </td>
                        <td>{user.school_name}</td>
                        <td>
                          <span style={{ fontWeight: 'bold' }}>
                            {user.registrations.length !== 0 && (
                              <>
                                {t('registeredChild')}
                                <span style={{ fontStyle: 'italic' }}>
                                  {user.registrations.length !== 0 &&
                                    user.registrations[0].food_type_is &&
                                    `- ${i18n.language === 'en' ? user.registrations[0].food_type_en : user.registrations[0].food_type_is}`}
                                </span>
                              </>
                            )}
                          </span>
                          <span style={{ fontStyle: 'italic' }}>
                            {user.extra_products && user.registrations.length !== 0 && (
                              <>
                                <br />
                                {/* <span>
                                  {user.extra_products.map((extra: any) => {
                                    if (extra.is_registered || extra.has_resigned) {
                                      return <span>{t('registeredExtraProducts')}:</span>;
                                    }
                                    return null;
                                  })}
                                </span> */}
                                <ul>
                                  {user.extra_products.map((extra: any) => {
                                    if (extra.is_registered) {
                                      // if (extra.has_resigned) {
                                      return (
                                        <>
                                          {/* <span>{t('registeredExtraProducts')}:</span> */}
                                          <li key={extra.product_id}>
                                            {extra.product_name} {extra.has_resigned ? ` - ${t('extraUnRegistered')} ${dayjs(extra.end_date).format('DD.MM.YYYY')}` : ''}
                                          </li>
                                        </>
                                      );
                                      // }
                                      //  return <li key={extra.product_id}>{extra.product_name} </li>;
                                    }
                                    return null;
                                  })}
                                </ul>
                              </>
                            )}
                          </span>
                          {user.registrations.length === 0 && (
                            <Button type="button" variant="primary" style={{ padding: '6px 12px' }} onClick={() => onHandleRegisterUser(user)}>
                              <FontAwesomeIcon icon={faPlusSquare} />
                              <span className="d-none d-sm-inline"> {t('applyForMeal')}</span>
                            </Button>
                          )}
                        </td>
                        {!childrenWithoutRegistration && <td>{user.registrations.length !== 0 && <span>{user.registrations[0].pin_number}</span>}</td>}
                        <td>
                          {user.registrations.length !== 0 && user.registrations[0].has_resigned === 0 && (
                            <span className="d-flex">
                              <OverlayTrigger placement="top" delay={{ show: 250, hide: 400 }} overlay={renderEditButtonTooltip}>
                                <Button type="button" variant="link" className="btn-edit" onClick={() => onHandleEditUser(user)}>
                                  <FontAwesomeIcon icon={faPencilAlt} />
                                </Button>
                              </OverlayTrigger>
                              <OverlayTrigger placement="top" delay={{ show: 250, hide: 400 }} overlay={renderDeleteButtonTooltip}>
                                <Button type="button" variant="link" className="btn-delete" onClick={() => onHandleDeleteUser(user)}>
                                  <FontAwesomeIcon icon={faTimesCircle} />
                                </Button>
                              </OverlayTrigger>
                            </span>
                          )}
                          {user.registrations.length !== 0 && user.registrations[0].has_resigned === 1 && (
                            <>
                              {t('childHasBeenUnRegistered')} <br />
                              {t('extraUnRegistered')} {user.registrations[0].registration_to !== null ? dayjs(user.registrations[0].registration_to).format('DD.MM.YYYY') : ''}
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        )
      )}
    </>
  );
};

export default Summary;
